import { useState, useCallback, useEffect } from 'react';
import { 
  fetchSyncDetails as fetchSyncDetailsApi, 
  fetchCourseDetails,
  getLatestSyncDetails  
} from '../api/syncApi';
import { toast } from 'react-toastify';
import logger from '../utils/logger';

/**
 * @param {boolean} canReadSyncData Whether the current account may call the
 *   sync detail endpoints at all. These are admin-only on the server, so this
 *   is not simply "is logged in" — see SyncDataContext.
 */
export const useSyncData = (canReadSyncData) => {
  // State for sync details
  const [lastUserSync, setLastUserSync] = useState(null);
  const [userStats, setUserStats] = useState({});
  const [userSyncHistory, setUserSyncHistory] = useState([]);
  
  const [lastCourseSync, setLastCourseSync] = useState(null);
  const [courseStats, setCourseStats] = useState([]);
  const [courseSyncHistory, setCourseSyncHistory] = useState([]);
  
  const [lastAttemptAnswersSync, setLastAttemptAnswersSync] = useState(null);
  const [attemptAnswersSyncHistory, setAttemptAnswersSyncHistory] = useState([]);
  
  const [lastSingleAttemptsSync, setLastSingleAttemptsSync] = useState(null);
  const [singleAttemptsSyncHistory, setSingleAttemptsSyncHistory] = useState([]);
  
  const [lastBatchSync, setLastBatchSync] = useState(null);
  const [batchStats, setBatchStats] = useState({});
  const [batchSyncHistory, setBatchSyncHistory] = useState([]);
  
  // Add content sync state
  const [lastContentSync, setLastContentSync] = useState(null);
  const [contentStats, setContentStats] = useState({});
  const [contentSyncHistory, setContentSyncHistory] = useState([]);
  
  // Loading states
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingAttemptAnswers, setIsLoadingAttemptAnswers] = useState(true);
  const [isLoadingSingleAttempts, setIsLoadingSingleAttempts] = useState(true);
  const [isLoadingBatches, setIsLoadingBatches] = useState(true);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSync, setSelectedSync] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [courseDetails, setCourseDetails] = useState(null);
  const [loadingDetailsId, setLoadingDetailsId] = useState(null);
  
  const fetchSyncDetails = useCallback(async (syncType, setIsLoading, setLastSync, setHistory, setStats = null) => {
    setIsLoading(true);
    try {
      logger.log(`Fetching ${syncType} details...`);
      const details = await getLatestSyncDetails(syncType);
      logger.log(`${syncType} details received:`, details);
      
      // Check if details has the expected properties
      if (!details) {
        throw new Error(`Invalid response for ${syncType}`);
      }
      
      setLastSync(details.lastSync);
      setHistory(details.syncHistory || []);
      
      if (setStats && details) {
        // Set stats if the setter function is provided
        if (syncType === 'content-sync' || syncType === 'content') {
          logger.log('Setting content stats:', details.contentStats);
          setStats(details.contentStats || {});
        } else {
          setStats(details.stats || {});
        }
      } 
    } catch (error) {
      logger.error(`Error fetching ${syncType} details:`, error);
      
      // Custom error message for content sync
      if (syncType === 'content-sync' || syncType === 'content') {
        toast.error(`Failed to load content-sync details. ${error.response?.data?.message || error.message}`);
      } else {
        toast.error(`Failed to load ${syncType} sync details.`);
      }
      
      setLastSync(null);
      setHistory([]);
      if (setStats) setStats({}); 
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Fetch all sync data
  const fetchAllSyncData = useCallback(() => {
    fetchSyncDetails('users', setIsLoadingUser, setLastUserSync, setUserSyncHistory, setUserStats);
    fetchSyncDetails('courses', setIsLoadingCourses, setLastCourseSync, setCourseSyncHistory, setCourseStats);
    fetchSyncDetails('attempt-answers-sync', setIsLoadingAttemptAnswers, setLastAttemptAnswersSync, setAttemptAnswersSyncHistory);
    fetchSyncDetails('single-attempts-sync', setIsLoadingSingleAttempts, setLastSingleAttemptsSync, setSingleAttemptsSyncHistory);
    fetchSyncDetails('batch-sync', setIsLoadingBatches, setLastBatchSync, setBatchSyncHistory, setBatchStats);
    fetchSyncDetails('content-sync', setIsLoadingContent, setLastContentSync, setContentSyncHistory, setContentStats);
  }, [fetchSyncDetails]);
  
  // Load data when authentication changes
  useEffect(() => {
    if (canReadSyncData) {
      fetchAllSyncData();
    }
  }, [canReadSyncData, fetchAllSyncData]);
  
  // Detail view handlers
  const handleViewDetails = async (syncId) => {
    try {
      setLoadingDetailsId(syncId);
      const syncDetails = await fetchSyncDetailsApi(syncId);
      setSelectedSync(syncDetails);
      setShowDetailsModal(true);
    } catch (err) {
      logger.error(`Error fetching details for sync ${syncId}:`, err);
      const errorMsg = err.response?.data?.error || err.message || `Failed to fetch details for sync ${syncId}`;
      toast.error(errorMsg);
      setShowDetailsModal(false);
      setSelectedSync(null);
    } finally {
      setLoadingDetailsId(null);
    }
  };
  
  const handleViewCourseDetails = async () => {
    try {
      const details = await fetchCourseDetails();
      setCourseDetails(details);
      setShowCourseModal(true);
    } catch (err) {
      logger.error('Error fetching course details:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch course details';
      toast.error(errorMsg);
      setShowCourseModal(false);
      setCourseDetails(null);
    }
  };
  
  return {
    // Sync data
    lastUserSync,
    userStats,
    userSyncHistory,
    lastCourseSync,
    courseStats,
    courseSyncHistory,
    lastAttemptAnswersSync,
    attemptAnswersSyncHistory,
    lastSingleAttemptsSync,
    singleAttemptsSyncHistory,
    lastBatchSync,
    batchStats,
    batchSyncHistory,
    lastContentSync,
    contentStats,
    contentSyncHistory,
    
    // Loading states
    isLoadingUser,
    isLoadingCourses,
    isLoadingAttemptAnswers,
    isLoadingSingleAttempts,
    isLoadingBatches,
    isLoadingContent,
    
    // Modal states and handlers
    showDetailsModal,
    setShowDetailsModal,
    selectedSync,
    showCourseModal,
    setShowCourseModal,
    courseDetails,
    loadingDetailsId,
    
    // Functions
    handleViewDetails,
    handleViewCourseDetails,
    fetchAllSyncData
  };
};

